import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { BasicCollapsible, Divider } from '@vp/swan';
import filter from 'lodash/filter';
import find from 'lodash/find';
import { useTranslations } from '@vp/om-translations';
import {
  aggregateTaxAwarePrices,
  ClusivitySeparatedPrices,
} from '@vp/om-tax-display';
import {
  type AttributeComponent,
  CartItemAttributeSummary as CartItemAttributeSummaryComponent,
} from '@vp/component-library';
import { DiscountSummary } from '../pricing/DiscountSummary';
import { TaxDisplayContext } from '../shared/taxDisplay/TaxDisplayContext';
import useSiteData from '../../hooks/useSiteData';
import { currencyLocaleFormatter } from '../../util/formatter';

interface CartItemAttributeSummaryProps {
  cartItemId: string;
  pricingData: any;
  productAttributes: any[];
}

interface PricingComponent {
  // identifier mapped from the VC response
  id: string;
  // display name (merchandising attribute data layered on top of the original VC component name)
  name: string;
  grossDiscountedCents: number;
  netDiscountedCents: number;
  grossListCents: number;
  netListCents: number;
  type?: string;
}

interface VistaCartPricingComponent {
  name: string;
  grossDiscountedCents: number;
  netDiscountedCents: number;
  grossListCents: number;
  netListCents: number;
  type?: string;
  pricingAttributeName?: string;
  netPricingAvailable?: boolean;
}

interface Subtotals {
  [key: string]: any;
}

const CartItemAttributeSummary = ({
  cartItemId,
  pricingData,
  productAttributes,
}: CartItemAttributeSummaryProps) => {
  const [cartMessages] = useTranslations(['cartMessages']);
  const { uiLocalization } = useSiteData();
  const currencyLocale = currencyLocaleFormatter(
    uiLocalization && uiLocalization.locale
  );
  const siteLocale = uiLocalization && uiLocalization.locale;

  const { clusivitySeparatedItemPrices, taxDisplayContext } =
    useContext(TaxDisplayContext) || {};

  const subtotalRequests: Subtotals = [];
  clusivitySeparatedItemPrices[cartItemId].forEach(
    (prices: ClusivitySeparatedPrices<PricingComponent>) => {
      subtotalRequests.push(
        prices.exclusivePrices.map(price => ({
          key: price.name,
        }))
      );
    }
  );

  const aggregated = aggregateTaxAwarePrices(
    clusivitySeparatedItemPrices[cartItemId],
    'item',
    'default',
    subtotalRequests.flat(),
    taxDisplayContext
  );

  const pricedComponents: VistaCartPricingComponent[] = [];

  pricingData.components.forEach((component: any) => {
    if (
      component.grossListCents &&
      component.grossListCents > 0 &&
      component.grossDiscountedCents &&
      component.grossDiscountedCents > 0
    ) {
      const {
        taxAwareListCents: listCents,
        taxAwareDiscountedCents: discountedCents,
      } = aggregated.subtotals.get(component.name) || {};

      pricedComponents.push({
        ...component,
        listCents,
        discountedCents,
        name:
          component.name === 'Base Price: '
            ? cartMessages.basePrice
            : component.name,
        labelFormat: {
          fontSize: 1,
        },
        priceFormat: {
          fontSize: 1,
        },
      });
    }
  });

  // Next, find all attributes that have display values.
  const displayAttributes = filter(
    productAttributes,
    attribute => attribute.keyDisplayName && attribute.valueDisplayName
  );

  // Finally, rule out any attributes that have pricing comonents or no display values.
  const unpricedDisplayAttributes: AttributeComponent[] = filter(
    displayAttributes,
    displayAttribute => {
      const attributeToPricingComponentKey = `${displayAttribute.key}: ${displayAttribute.value}`;
      const pricingComponent = find(
        pricedComponents,
        component =>
          component.pricingAttributeName === attributeToPricingComponentKey
      );
      return !pricingComponent;
    }
  ).map(attr => ({
    name: `${attr.keyDisplayName}: ${attr.valueDisplayName}`,
    labelFormat: {
      fontSize: 1,
    },
    priceFormat: {
      fontSize: 1,
    },
  }));

  const savedCents =
    aggregated.taxAwareListCents - aggregated.taxAwareDiscountedCents;

  /*
  If no display attributes exist for the line item, then we opt out of displaying the Selected Options Accordion for the line item.

  This allows the client of Vistacart to enable whether or not Selected Options should be shown.

  In the case that display attributes for options ARE provided, we believe that:
  It is best for the CartUI to always display priced attributes which sum to the item total, regardless of whether or not there is
  untranslated priced attribute.
  */
  if (!displayAttributes.length || displayAttributes.length === 0) {
    return null;
  }
  const hasAttributes =
    unpricedDisplayAttributes.length ||
    pricedComponents.length ||
    savedCents > 0;

  if (!hasAttributes) return null;

  return (
    <BasicCollapsible
      heading={cartMessages.selectedOptions}
      defaultExpanded={false}
      role="note"
      fullBleed
    >
      <CartItemAttributeSummaryComponent
        itemId={cartItemId}
        attributeComponents={[
          ...pricedComponents,
          ...unpricedDisplayAttributes,
        ]}
        fractionDigits={pricingData.fractionDigits}
        currencyCode={pricingData.currency}
        locale={siteLocale}
        isLoading={false}
      />
      {savedCents && savedCents > 0 ? (
        <Divider marginTop={1} marginBottom={4} />
      ) : undefined}
      <DiscountSummary
        fractionDigits={pricingData.fractionDigits}
        savedCents={savedCents}
        currency={pricingData.currency}
        currencyLocale={currencyLocale}
      />
    </BasicCollapsible>
  );
};

CartItemAttributeSummary.propTypes = {
  cartItemId: PropTypes.string.isRequired,
  pricingData: PropTypes.object.isRequired,
  productAttributes: PropTypes.array.isRequired,
};

export default CartItemAttributeSummary;
